import { Controller } from "@hotwired/stimulus"
import { captureBackdropDrops, carriesFiles, flashFileWarning, partitionByAccept } from "../../utils/file-upload.util"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  unsupportedFileText: "Unsupported file type and was skipped: "
}

// Connects to data-controller="documents--document-upload"
// Single-file upload for the create-by-upload offcanvas shared by every
// extractable document type (interný doklad, prijatá objednávka) — see
// app/views/documents/shared/_upload_offcanvas.html.erb.
// Mirrors the expenses batch-upload sidebar but for ONE file: the form is a
// plain multipart POST (Turbo follows the redirect), so this controller only
// handles the file picker, the chosen-file preview, and enabling the submit
// button. Resets itself whenever the offcanvas is hidden.
export default class extends Controller {
  static targets = ["fileInput", "submitButton", "placeholder", "chosen", "filename", "filesize"]

  static values = {
    translations: { type: Object, default: {} }
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.resetHandler = () => this.reset()
    this.element.addEventListener("hidden.bs.offcanvas", this.resetHandler)

    this._releaseBackdropDrops = captureBackdropDrops({
      element: this.element,
      enabled: () => this.hasFileInputTarget,
      onFiles: (files) => this.takeFiles(files),
      setDragging: (active) => this.#setDragging(active)
    })
  }

  disconnect() {
    this._releaseBackdropDrops?.()
    this.element.removeEventListener("hidden.bs.offcanvas", this.resetHandler)
  }

  // Highlight the panel while a file hovers over it.
  dragOver(event) {
    if (!this.#acceptsDrop(event)) return

    event.preventDefault()
    // Without this the cursor can read "move"/"link" when a modifier is held.
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
    this.#setDragging(true)
  }

  dragLeave(event) {
    // dragleave also fires when the pointer crosses onto a child element —
    // only drop the highlight once it has really left the panel.
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) return

    this.#setDragging(false)
  }

  // Bound to the whole offcanvas so a drop that misses the artwork can't fall
  // through to the browser default and navigate the tab to the dropped file.
  drop(event) {
    if (!this.#acceptsDrop(event)) return

    event.preventDefault()
    this.#setDragging(false)
    this.takeFiles(Array.from(event.dataTransfer.files))
  }

  // One document per upload, so only the first acceptable file is taken.
  takeFiles(incoming) {
    if (!this.hasFileInputTarget) return

    const [first] = this.#filterAccepted(incoming)
    if (!first) return

    const transfer = new DataTransfer()
    transfer.items.add(first)
    this.fileInputTarget.files = transfer.files
    this.#renderChosen(first)
  }

  // Only claim drags that carry files — dragging selected text across the panel
  // must keep its normal behaviour — and only when there is an input to fill.
  // Out of AI quota this offcanvas renders the upgrade pitch with no form at
  // all, and claiming a drop there would throw on the missing target.
  #acceptsDrop(event) {
    return this.hasFileInputTarget && carriesFiles(event)
  }

  #setDragging(active) {
    this.element.classList.toggle("file-drop--active", active)
  }

  // A drop bypasses the input's `accept` filter, and the picker can be switched
  // to "All files" on some platforms — enforce it on both paths instead of
  // letting an unsupported file reach (and fail) the extraction.
  #filterAccepted(incoming) {
    const { accepted, rejected } = partitionByAccept(incoming, this.fileInputTarget.accept)
    for (const file of rejected) {
      flashFileWarning(`${this.#texts.unsupportedFileText}${file.name}`)
    }
    return accepted
  }

  // Action handler for the picker. Everything it hands us still has to clear
  // `accept`, so route it through the same filter a drop goes through.
  fileChanged() {
    const file = this.fileInputTarget.files[0]
    if (!file || this.#filterAccepted([file]).length === 0) {
      this.reset()
      return
    }

    this.#renderChosen(file)
  }

  #renderChosen(file) {
    this.filenameTarget.textContent = file.name
    if (this.hasFilesizeTarget) {
      this.filesizeTarget.textContent = this.formatBytes(file.size)
    }
    this.placeholderTarget.classList.add("d-none")
    this.chosenTarget.classList.remove("d-none")
    this.submitButtonTarget.disabled = false
  }

  clearFile() {
    this.reset()
  }

  reset() {
    // Fires on hidden.bs.offcanvas too, which the locked (out-of-quota) variant
    // of this panel also emits — and that one renders no form at all.
    this.#setDragging(false)
    if (!this.hasFileInputTarget) return

    this.fileInputTarget.value = ""
    this.filenameTarget.textContent = ""
    if (this.hasFilesizeTarget) {
      this.filesizeTarget.textContent = ""
    }
    this.placeholderTarget.classList.remove("d-none")
    this.chosenTarget.classList.add("d-none")
    this.submitButtonTarget.disabled = true
  }

  // Mirrors the file-size label the expenses preview shows (e.g. "613 KB").
  formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}
